import React, { useState, useRef, useEffect } from 'react';
import {
  Box, Fab, Drawer, Typography, TextField, IconButton,
  CircularProgress, Paper, Tooltip, Divider, Table,
  TableBody, TableCell, TableContainer, TableHead, TableRow,
} from '@mui/material';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip as ReTooltip, ResponsiveContainer,
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar,
} from 'recharts';

interface Message {
  role: 'user' | 'assistant';
  content: string;
  loading?: boolean;
}

const API_URL = process.env.REACT_APP_API_URL || '';
const COLORS = ['#1976d2', '#e91e63', '#ff9800', '#4caf50', '#9c27b0', '#00bcd4'];

type ChartType = 'bar' | 'line' | 'radar';

function extractChartBlocks(text: string) {
  const results: Array<{ type: ChartType; data: any[]; title?: string }> = [];
  const re = /```chart-(bar|line|radar)\s*\n([\s\S]*?)```/g;
  let m;
  while ((m = re.exec(text)) !== null) {
    try { results.push({ type: m[1] as ChartType, ...JSON.parse(m[2].trim()) }); } catch { /* skip */ }
  }
  return results;
}

function stripChartBlocks(text: string) {
  return text.replace(/```chart-(bar|line|radar)\s*\n[\s\S]*?```/g, '');
}

function ChartRenderer({ block }: { block: { type: ChartType; data: any[]; title?: string } }) {
  const { type, data, title } = block;
  const keys = data.length > 0 ? Object.keys(data[0]).filter(k => k !== 'name' && k !== 'subject') : [];
  return (
    <Box sx={{ mt: 1 }}>
      {title && <Typography variant="caption" color="text.secondary">{title}</Typography>}
      <ResponsiveContainer width="100%" height={220}>
        {type === 'radar' ? (
          <RadarChart data={data} margin={{ top: 10, right: 20, bottom: 10, left: 20 }}>
            <PolarGrid />
            <PolarAngleAxis dataKey="subject" tick={{ fontSize: 11 }} />
            <PolarRadiusAxis tick={{ fontSize: 9 }} />
            <ReTooltip />
            {keys.map((k, i) => (
              <Radar key={k} dataKey={k} stroke={COLORS[i % COLORS.length]} fill={COLORS[i % COLORS.length]} fillOpacity={0.3} />
            ))}
          </RadarChart>
        ) : type === 'bar' ? (
          <BarChart data={data} margin={{ top: 4, right: 8, bottom: 4, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
            <XAxis dataKey="name" tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 11 }} width={40} />
            <ReTooltip />
            {keys.map((k, i) => <Bar key={k} dataKey={k} fill={COLORS[i % COLORS.length]} radius={[3,3,0,0]} />)}
          </BarChart>
        ) : (
          <LineChart data={data} margin={{ top: 4, right: 8, bottom: 4, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
            <XAxis dataKey="name" tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 11 }} width={40} />
            <ReTooltip />
            {keys.map((k, i) => <Line key={k} type="monotone" dataKey={k} stroke={COLORS[i % COLORS.length]} strokeWidth={2} dot={false} />)}
          </LineChart>
        )}
      </ResponsiveContainer>
    </Box>
  );
}

function MessageContent({ content, isUser }: { content: string; isUser: boolean }) {
  if (isUser) return <Typography variant="body2" sx={{ lineHeight: 1.6 }}>{content}</Typography>;

  const charts = extractChartBlocks(content);
  const cleanText = stripChartBlocks(content);

  return (
    <Box>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          p: ({ children }) => <Typography variant="body2" sx={{ mb: 0.5, lineHeight: 1.6 }}>{children}</Typography>,
          ul: ({ children }) => <Box component="ul" sx={{ pl: 2, my: 0.5 }}>{children}</Box>,
          ol: ({ children }) => <Box component="ol" sx={{ pl: 2, my: 0.5 }}>{children}</Box>,
          li: ({ children }) => <Typography component="li" variant="body2" sx={{ lineHeight: 1.6 }}>{children}</Typography>,
          h1: ({ children }) => <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 1 }}>{children}</Typography>,
          h2: ({ children }) => <Typography variant="subtitle2" fontWeight="bold" sx={{ mt: 1 }}>{children}</Typography>,
          h3: ({ children }) => <Typography variant="body2" fontWeight="bold" sx={{ mt: 0.5 }}>{children}</Typography>,
          table: ({ children }) => (
            <TableContainer component={Paper} variant="outlined" sx={{ my: 1 }}>
              <Table size="small">{children}</Table>
            </TableContainer>
          ),
          thead: ({ children }) => <TableHead sx={{ bgcolor: 'primary.main' }}>{children}</TableHead>,
          tbody: ({ children }) => <TableBody>{children}</TableBody>,
          tr: ({ children }) => <TableRow hover>{children}</TableRow>,
          th: ({ children }) => (
            <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.72rem', py: 0.5, px: 1 }}>{children}</TableCell>
          ),
          td: ({ children }) => (
            <TableCell sx={{ fontSize: '0.72rem', py: 0.5, px: 1 }}>{children}</TableCell>
          ),
          code: ({ children, className }) => {
            const isBlock = !!className?.includes('language-');
            return isBlock
              ? <Box component="pre" sx={{ bgcolor: 'action.hover', p: 1, borderRadius: 1, fontSize: '0.7rem', overflow: 'auto', my: 0.5 }}><code>{children}</code></Box>
              : <Box component="code" sx={{ bgcolor: 'action.hover', px: 0.5, borderRadius: 0.5, fontSize: '0.75rem', fontFamily: 'monospace' }}>{children}</Box>;
          },
        }}
      >
        {cleanText}
      </ReactMarkdown>
      {charts.map((chart, i) => <ChartRenderer key={i} block={chart} />)}
    </Box>
  );
}

const AiChatWidget: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([
    { role: 'assistant', content: 'Olá! Sou seu consultor estratégico virtual.\n\nPosso ajudar com **metas**, **SWOT** e **planos de ação**. Também gero **tabelas** e **gráficos** dos seus dados.\n\nO que você gostaria de saber?' },
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || loading) return;
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: text }, { role: 'assistant', content: '', loading: true }]);
    setLoading(true);

    const token = localStorage.getItem('@ThinkWay:token');
    try {
      const res = await fetch(`${API_URL}/api/ai/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'text/event-stream',
          ...(token ? { Authorization: token.startsWith('Bearer') ? token : `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ mensagem: text }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const reader = res.body!.getReader();
      const decoder = new TextDecoder();
      let accumulated = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        for (const line of decoder.decode(value, { stream: true }).split('\n')) {
          if (!line.startsWith('data:')) continue;
          const raw = line.slice(5).trim();
          if (!raw || raw === '{}') continue;
          try {
            const parsed = JSON.parse(raw);
            if (parsed.content) {
              accumulated += parsed.content;
              setMessages(prev => { const u = [...prev]; u[u.length - 1] = { role: 'assistant', content: accumulated, loading: false }; return u; });
            }
            if (parsed.error) throw new Error(parsed.message || 'Erro');
          } catch { /* linha parcial */ }
        }
      }
    } catch (err: any) {
      setMessages(prev => { const u = [...prev]; u[u.length - 1] = { role: 'assistant', content: `**Erro:** ${err.message}`, loading: false }; return u; });
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); }
  };

  return (
    <>
      {/* FAB oculto quando drawer está aberto — evita sobrepor botão de enviar */}
      {!open && (
        <Tooltip title="Consultor Estratégico IA" placement="left">
          <Fab color="primary" onClick={() => setOpen(true)} sx={{ position: 'fixed', bottom: 24, right: 24, zIndex: 1300 }}>
            <SmartToyIcon />
          </Fab>
        </Tooltip>
      )}

      <Drawer
        anchor="right"
        open={open}
        onClose={() => setOpen(false)}
        PaperProps={{ sx: { width: { xs: '100vw', sm: 440 }, display: 'flex', flexDirection: 'column' } }}
      >
        <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', bgcolor: 'primary.main', color: 'white', flexShrink: 0 }}>
          <SmartToyIcon sx={{ mr: 1 }} />
          <Typography variant="subtitle1" sx={{ flexGrow: 1, fontWeight: 600 }}>Consultor Estratégico IA</Typography>
          <Tooltip title="Reiniciar">
            <IconButton size="small" onClick={() => setMessages([{ role: 'assistant', content: 'Conversa reiniciada. Como posso ajudar?' }])} sx={{ color: 'white', mr: 0.5 }}>
              <RestartAltIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <IconButton size="small" onClick={() => setOpen(false)} sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Divider />

        <Box sx={{ flex: 1, overflowY: 'auto', p: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {messages.map((msg, i) => (
            <Box key={i} sx={{ display: 'flex', justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start' }}>
              <Paper
                elevation={1}
                sx={{
                  px: 1.5, py: 1, maxWidth: '92%',
                  bgcolor: msg.role === 'user' ? 'primary.main' : 'background.paper',
                  color: msg.role === 'user' ? 'white' : 'text.primary',
                  borderRadius: msg.role === 'user' ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                  wordBreak: 'break-word', overflowX: 'auto',
                }}
              >
                {msg.loading
                  ? <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><CircularProgress size={14} color="inherit" /><Typography variant="body2">Consultando...</Typography></Box>
                  : <MessageContent content={msg.content} isUser={msg.role === 'user'} />}
              </Paper>
            </Box>
          ))}
          <div ref={bottomRef} />
        </Box>

        <Divider />

        <Box sx={{ p: 1.5, display: 'flex', gap: 1, alignItems: 'flex-end', flexShrink: 0 }}>
          <TextField
            fullWidth multiline maxRows={4} size="small"
            placeholder="Pergunte sobre metas, SWOT, planos..."
            value={input} onChange={e => setInput(e.target.value)} onKeyDown={handleKeyDown} disabled={loading}
            sx={{ '& .MuiInputBase-root': { borderRadius: 3 } }}
          />
          <IconButton
            color="primary" onClick={sendMessage} disabled={!input.trim() || loading}
            sx={{
              flexShrink: 0, width: 40, height: 40, borderRadius: 2,
              bgcolor: input.trim() && !loading ? 'primary.main' : 'action.disabledBackground',
              color: input.trim() && !loading ? 'white' : 'text.disabled',
              '&:hover': { bgcolor: 'primary.dark' },
            }}
          >
            {loading ? <CircularProgress size={18} color="inherit" /> : <SendIcon fontSize="small" />}
          </IconButton>
        </Box>
      </Drawer>
    </>
  );
};

export default AiChatWidget;
